// t() reads the page language, which the compiler cannot see, and this stays
// on screen while the language changes.
"use no memo";
import { useState, useSyncExternalStore } from "react";
import CaretDown from "~icons/ph/caret-down";
import { t } from "../i18n";

interface InstallPrompt extends Event {
  prompt(): Promise<{ outcome: "accepted" | "dismissed" }>;
}
declare global {
  interface WindowEventMap {
    beforeinstallprompt: InstallPrompt;
  }
}
const display = matchMedia("(display-mode: standalone)");
let installed =
  display.matches ||
  ("standalone" in navigator && navigator.standalone === true);
let prompt: InstallPrompt | null = null;
const listeners = new Set<() => void>();
const changed = () => listeners.forEach((listener) => listener());
window.addEventListener("beforeinstallprompt", (event) => {
  event.preventDefault();
  prompt = event;
  changed();
});
window.addEventListener("appinstalled", () => {
  installed = true;
  prompt = null;
  changed();
});
display.addEventListener("change", () => {
  installed = display.matches;
  changed();
});
const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};
export const ios =
  /iPad|iPhone|iPod/.test(navigator.userAgent) ||
  (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
export function useInstalled() {
  return useSyncExternalStore(subscribe, () => installed);
}

// Installation never requests notification permission or transfers credentials.
export function InstallGuide({
  signedIn,
  onSignIn,
  onRecovery,
}: {
  signedIn: boolean;
  onSignIn: () => void;
  onRecovery: () => void;
}) {
  const installed = useInstalled();
  const available = useSyncExternalStore(subscribe, () => prompt);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const install = async () => {
    if (!available || busy) return;
    setBusy(true);
    setError("");
    try {
      await available.prompt();
      // A prompt is single use, including when it is dismissed.
      prompt = null;
      changed();
    } catch {
      prompt = null;
      changed();
      setError(
        "تعذر فتح التثبيت. استخدم قائمة المتصفح لإضافة رنّة إلى الشاشة الرئيسية.",
      );
    } finally {
      setBusy(false);
    }
  };
  return (
    <section
      className="install-guide"
      aria-label={t(installed ? "تطبيق رنّة" : "تثبيت رنّة")}
    >
      <details open={installed && !signedIn ? true : undefined}>
        <summary>
          <CaretDown width={12} height={12} aria-hidden="true" />
          {t(installed ? "تطبيق رنّة" : "تثبيت رنّة")}
        </summary>
        <p>
          {t(
            installed
              ? signedIn
                ? "أنت تستخدم تطبيق رنّة. فعّل الإشعارات من الإعدادات."
                : "لديك حساب في المتصفح؟ ادخل بمفتاح المرور أو رمز الاستعادة للاحتفاظ بأصدقائك."
              : ios
                ? "على iPhone أو iPad، افتح رنّة في Safari، ثم مشاركة، ثم إضافة إلى الشاشة الرئيسية. افتح رنّة من الأيقونة لتفعيل الإشعارات. يتطلب iOS أو iPadOS 16.4 أو أحدث."
                : /Android/.test(navigator.userAgent)
                  ? "ثبّت رنّة من الزر أو من قائمة المتصفح: تثبيت التطبيق أو إضافة إلى الشاشة الرئيسية. ثم افتحه وفعّل الإشعارات من الإعدادات."
                  : "ثبّت رنّة من الزر أو من قائمة المتصفح إذا كان التثبيت متاحًا، ثم فعّل الإشعارات من الإعدادات.",
          )}
        </p>
        {!installed && (
          <p>
            {t(
              "قبل التثبيت، أضف مفتاح مرور أو احفظ رمز الاستعادة من «الدخول والأجهزة». إذا طلب التطبيق الدخول، استخدم أيًا منهما؛ قد يكون دخوله منفصلًا عن المتصفح.",
            )}
          </p>
        )}
        <p>
          {t(
            "تحتاج رنّة إلى الإنترنت للمكالمات والرسائل. قد تتأخر الإشعارات بسبب الشبكة أو إعدادات النظام.",
          )}
        </p>
        <div className="install-actions">
          {!installed && available && (
            <button
              className="button secondary small"
              disabled={busy}
              aria-busy={busy}
              onClick={() => void install()}
            >
              {t("تثبيت رنّة")}
            </button>
          )}
          {signedIn && !installed && (
            <button className="quiet-button" onClick={onRecovery}>
              {t("الدخول والأجهزة")}
            </button>
          )}
          {!signedIn && (
            <button className="button secondary small" onClick={onSignIn}>
              {t("تسجيل الدخول")}
            </button>
          )}
        </div>
        {error && (
          <p className="form-error" role="alert">
            {t(error)}
          </p>
        )}
      </details>
    </section>
  );
}
